import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Paper,
  Typography,
  Box,
  Alert,
  TextField,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Divider,
} from '@mui/material';
import {
  Calculate as CalculateIcon,
  Save as SaveIcon,
  FileDownload as ExportIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import ResponsiveTable from '../components/ResponsiveTable';
import SelectField from '../components/inputs/SelectField';
import { useAuth } from '../context/AuthContext';
import { useFeedback } from '../context/FeedbackContext';
import { hasPermission } from '../utils/permissions';
import { ROLES } from '../constants/roles';
import { DIAMOND_TYPES } from '../constants/appConstants';
import { useAsyncAction } from '../hooks/useAsyncAction';
import {
  DATE_PRESETS,
  resolveDatePresetRange,
  buildRequirementsCsv,
  downloadCsvFile,
} from '../utils/inventoryRequirementsDatePresets';
import {
  shouldLoadSavedRequirements,
  isValidRequirementsScope,
  savedRequirementsRequestParams,
} from '../utils/inventoryRequirementsLoad';

const DEFAULT_DEMAND_FILTERS = Object.freeze({
  includeSold: true,
  includeDelivered: true,
  includeReserved: true,
  includeDeleted: false,
});

const defaultParams = () => ({
  datePreset: '1m',
  dateFrom: '',
  dateTo: '',
  divider: 1,
  mode: 'formula',
  demandFilters: { ...DEFAULT_DEMAND_FILTERS },
});

function formatMetric(value, isCarat) {
  if (value == null) return '—';
  return isCarat ? Number(value).toFixed(2) : String(value);
}

const InventoryRequirements = ({ scope, isTabActive = true }) => {
  const { t } = useTranslation();
  const { isInternal, userRole, customPermissions } = useAuth();
  const { showSuccess, showError } = useFeedback();
  const { loading: actionLoading, run: runAction } = useAsyncAction();

  const isCarat = scope === DIAMOND_TYPES.NON_CERTIFIED;

  const canAccess = isInternal && (
    userRole === ROLES.MANAGER
    || hasPermission(userRole, 'manage_inventory_requirements', customPermissions)
  );

  const [params, setParamsState] = useState(defaultParams);
  const [rows, setRowsState] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [savedLoaded, setSavedLoaded] = useState(false);

  const setParams = useCallback((patch) => {
    setParamsState((prev) => ({ ...prev, ...patch }));
  }, []);

  const setRows = useCallback((nextRowsOrFn) => {
    setRowsState((prev) => (
      typeof nextRowsOrFn === 'function' ? nextRowsOrFn(prev) : nextRowsOrFn
    ));
  }, []);

  const hydrateFromSaved = useCallback((doc) => {
    if (!doc || doc.scope !== scope) return;

    setParamsState({
      datePreset: 'custom',
      dateFrom: doc.dateFrom ? new Date(doc.dateFrom).toISOString().slice(0, 10) : '',
      dateTo: doc.dateTo ? new Date(doc.dateTo).toISOString().slice(0, 10) : '',
      divider: doc.divider ?? 1,
      mode: doc.mode || 'formula',
      demandFilters: {
        ...DEFAULT_DEMAND_FILTERS,
        ...(doc.demandFilters || {}),
      },
    });
    setRowsState(doc.rows || []);
  }, [scope]);

  useEffect(() => {
    if (!shouldLoadSavedRequirements({ canAccess, isTabActive, savedLoaded })) return undefined;

    let cancelled = false;
    setLoadingSaved(true);

    axios.get('/api/inventory-requirements', { params: savedRequirementsRequestParams(scope) })
      .then((res) => {
        if (cancelled) return;
        hydrateFromSaved(res.data);
        setSavedLoaded(true);
      })
      .catch(() => {
        if (!cancelled) showError(t('inventoryRequirements.loadFailed'));
      })
      .finally(() => {
        if (!cancelled) setLoadingSaved(false);
      });

    return () => { cancelled = true; };
  }, [canAccess, isTabActive, savedLoaded, scope, hydrateFromSaved, showError, t]);

  const resolvedDates = useMemo(() => {
    if (params.datePreset === 'custom') {
      return { dateFrom: params.dateFrom, dateTo: params.dateTo };
    }
    return resolveDatePresetRange(params.datePreset) || { dateFrom: '', dateTo: '' };
  }, [params.datePreset, params.dateFrom, params.dateTo]);

  const validateDivider = () => {
    const div = Number(params.divider);
    if (!Number.isFinite(div) || div <= 0) {
      showError(t('inventoryRequirements.invalidDivider'));
      return false;
    }
    return true;
  };

  const handleCalculate = () => runAction(async () => {
    if (!validateDivider()) return;

    try {
      const payload = {
        scope,
        divider: params.divider,
        mode: params.mode,
        dateFrom: resolvedDates.dateFrom || undefined,
        dateTo: resolvedDates.dateTo || undefined,
        includeSold: params.demandFilters.includeSold,
        includeDelivered: params.demandFilters.includeDelivered,
        includeReserved: params.demandFilters.includeReserved,
        includeDeleted: params.demandFilters.includeDeleted,
      };
      if (params.mode === 'manual') {
        payload.manualRows = rows.map((row) => ({
          shape: row.shape,
          band: row.band,
          measurement: row.measurement,
          manualValue: row.manualValue,
        }));
      }
      const res = await axios.post('/api/inventory-requirements/calculate', payload);
      setRows(res.data?.rows || []);
      showSuccess(t('inventoryRequirements.calculated'));
    } catch (err) {
      showError(err.response?.data?.error || t('inventoryRequirements.calculateFailed'));
    }
  });

  const handleSave = () => runAction(async () => {
    if (!validateDivider()) return;

    try {
      const payload = {
        scope,
        dateFrom: resolvedDates.dateFrom || null,
        dateTo: resolvedDates.dateTo || null,
        divider: Number(params.divider),
        mode: params.mode,
        demandFilters: params.demandFilters,
        rows,
      };
      await axios.post('/api/inventory-requirements', payload);
      showSuccess(t('inventoryRequirements.saved'));
    } catch (err) {
      showError(err.response?.data?.error || t('inventoryRequirements.saveFailed'));
    }
  });

  const handleExportCsv = () => {
    const headers = isCarat
      ? ['shape', 'measurement', 'caratPerStone', 'sold', 'delivered', 'reserved', 'deleted', 'demand', 'requirement', 'inStock', 'needToOrder']
      : ['shape', 'band', 'sold', 'delivered', 'reserved', 'deleted', 'demand', 'requirement', 'inStock', 'needToOrder'];

    const localizedHeaders = headers.map((h) => t(`inventoryRequirements.col.${h}`));
    const csv = buildRequirementsCsv(rows, localizedHeaders, (row) => {
      if (isCarat) {
        return [
          row.shape,
          row.measurement,
          row.caratPerStone ?? '',
          row.sold,
          row.delivered,
          row.reserved,
          row.deleted,
          row.demand,
          row.requirement,
          row.inStock,
          row.needToOrder,
        ];
      }
      return [
        row.shape,
        row.band,
        row.sold,
        row.delivered,
        row.reserved,
        row.deleted,
        row.demand,
        row.requirement,
        row.inStock,
        row.needToOrder,
      ];
    });
    const filename = `inventory-requirements-${scope}-${new Date().toISOString().slice(0, 10)}.csv`;
    downloadCsvFile(filename, csv);
  };

  const rowKey = useCallback(
    (row) => `${row.shape}|${row.band || row.measurement}`,
    []
  );

  const handleManualRequirementChange = useCallback((key, value) => {
    const num = value === '' ? null : Number(value);
    setRows((prev) => prev.map((row) => {
      if (rowKey(row) !== key) return row;
      const manualValue = Number.isFinite(num) ? num : null;
      const requirement = manualValue ?? row.requirement;
      const needToOrder = isCarat
        ? Math.max(0, Math.round((requirement - row.inStock) * 100) / 100)
        : Math.max(0, Math.ceil(requirement - row.inStock));
      return { ...row, manualValue, requirement, needToOrder };
    }));
  }, [setRows, rowKey, isCarat]);

  const columns = useMemo(() => {
    const base = [
      { id: 'shape', label: t('inventoryRequirements.col.shape'), sortable: false },
      isCarat
        ? { id: 'measurement', label: t('inventoryRequirements.col.measurement'), sortable: false }
        : { id: 'band', label: t('inventoryRequirements.col.band'), sortable: false },
      ...(isCarat ? [{
        id: 'caratPerStone',
        label: t('inventoryRequirements.col.caratPerStone'),
        sortable: false,
        render: (row) => (row.caratPerStone != null ? Number(row.caratPerStone).toFixed(2) : '—'),
      }] : []),
      { id: 'sold', label: t('inventoryRequirements.col.sold'), sortable: false,
        render: (row) => formatMetric(row.sold, isCarat) },
      { id: 'delivered', label: t('inventoryRequirements.col.delivered'), sortable: false,
        render: (row) => formatMetric(row.delivered, isCarat) },
      { id: 'reserved', label: t('inventoryRequirements.col.reserved'), sortable: false,
        render: (row) => formatMetric(row.reserved, isCarat) },
      { id: 'deleted', label: t('inventoryRequirements.col.deleted'), sortable: false,
        render: (row) => formatMetric(row.deleted, isCarat) },
      { id: 'demand', label: t('inventoryRequirements.col.demand'), sortable: false,
        render: (row) => formatMetric(row.demand, isCarat) },
      {
        id: 'requirement',
        label: t('inventoryRequirements.col.requirement'),
        sortable: false,
        render: (row) => {
          if (params.mode === 'manual') {
            return (
              <TextField
                size="small"
                type="number"
                value={row.manualValue ?? row.requirement ?? ''}
                onChange={(e) => handleManualRequirementChange(rowKey(row), e.target.value)}
                inputProps={{ min: 0, step: isCarat ? 'any' : 1 }}
                sx={{ width: 100 }}
              />
            );
          }
          return formatMetric(row.requirement, isCarat);
        },
      },
      { id: 'inStock', label: t('inventoryRequirements.col.inStock'), sortable: false,
        render: (row) => formatMetric(row.inStock, isCarat) },
      { id: 'needToOrder', label: t('inventoryRequirements.col.needToOrder'), sortable: false,
        render: (row) => (
          <Typography
            component="span"
            fontWeight={row.needToOrder > 0 ? 600 : 400}
            color={row.needToOrder > 0 ? 'error.main' : 'text.primary'}
          >
            {formatMetric(row.needToOrder, isCarat)}
          </Typography>
        ) },
    ];
    return base;
  }, [t, isCarat, params.mode, rowKey, handleManualRequirementChange]);

  const renderMobileCard = (row) => (
    <>
      <Typography variant="subtitle2" fontWeight={600}>
        {row.shape} {row.band || row.measurement}
        {isCarat && row.caratPerStone != null ? ` (${Number(row.caratPerStone).toFixed(2)} ct)` : ''}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('inventoryRequirements.col.demand')}: {formatMetric(row.demand, isCarat)}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {t('inventoryRequirements.col.inStock')}: {formatMetric(row.inStock, isCarat)}
      </Typography>
      <Typography variant="body2" fontWeight={600} color={row.needToOrder > 0 ? 'error.main' : 'text.primary'}>
        {t('inventoryRequirements.col.needToOrder')}: {formatMetric(row.needToOrder, isCarat)}
      </Typography>
    </>
  );

  if (!isValidRequirementsScope(scope)) {
    return (
      <Alert severity="error">
        {t('common.error')}
      </Alert>
    );
  }

  if (!canAccess) {
    return (
      <Alert severity="warning">
        {t('users.noPermission')}
      </Alert>
    );
  }

  const datePresetOptions = DATE_PRESETS.map((p) => ({
    value: p.id,
    label: t(`inventoryRequirements.datePreset.${p.id}`),
  }));

  const toggleDemandFilter = (key) => (event) => {
    const next = event.target.checked;
    const enabledCount = Object.values(params.demandFilters).filter(Boolean).length;
    if (!next && enabledCount <= 1) {
      showError(t('inventoryRequirements.atLeastOneDemandFilter'));
      return;
    }
    setParams({
      demandFilters: {
        ...params.demandFilters,
        [key]: next,
      },
    });
  };

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="flex-end">
          <SelectField
            label={t('inventoryRequirements.dateRange')}
            value={params.datePreset}
            onChange={(v) => setParams({ datePreset: v })}
            options={datePresetOptions}
            size="small"
            sx={{ minWidth: 160 }}
          />

          {params.datePreset === 'custom' && (
            <>
              <TextField
                label={t('inventoryRequirements.dateFrom')}
                type="date"
                size="small"
                value={params.dateFrom}
                onChange={(e) => setParams({ dateFrom: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 160 }}
              />
              <TextField
                label={t('inventoryRequirements.dateTo')}
                type="date"
                size="small"
                value={params.dateTo}
                onChange={(e) => setParams({ dateTo: e.target.value })}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 160 }}
              />
            </>
          )}

          <TextField
            label={t('inventoryRequirements.divider')}
            type="number"
            size="small"
            value={params.divider}
            onChange={(e) => setParams({ divider: e.target.value })}
            inputProps={{ min: 0.0001, step: 'any' }}
            sx={{ width: 120 }}
          />

          <ToggleButtonGroup
            size="small"
            exclusive
            value={params.mode}
            onChange={(_, v) => v && setParams({ mode: v })}
          >
            <ToggleButton value="formula">{t('inventoryRequirements.modeFormula')}</ToggleButton>
            <ToggleButton value="manual">{t('inventoryRequirements.modeManual')}</ToggleButton>
          </ToggleButtonGroup>

          <Box display="flex" gap={1} flexWrap="wrap" sx={{ ml: { md: 'auto' } }}>
            <Button
              variant="contained"
              size="small"
              startIcon={actionLoading ? <CircularProgress size={18} color="inherit" /> : <CalculateIcon />}
              onClick={handleCalculate}
              disabled={actionLoading}
            >
              {t('inventoryRequirements.calculate')}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={actionLoading || rows.length === 0}
            >
              {t('inventoryRequirements.save')}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ExportIcon />}
              onClick={handleExportCsv}
              disabled={rows.length === 0}
            >
              {t('inventoryRequirements.exportCsv')}
            </Button>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" gutterBottom>
          {t('inventoryRequirements.demandFiltersTitle')}
        </Typography>
        <FormGroup row sx={{ gap: { xs: 0, sm: 2 } }}>
          <FormControlLabel
            control={(
              <Checkbox
                size="small"
                checked={params.demandFilters.includeSold}
                onChange={toggleDemandFilter('includeSold')}
              />
            )}
            label={t('inventoryRequirements.demandFilter.sold')}
          />
          <FormControlLabel
            control={(
              <Checkbox
                size="small"
                checked={params.demandFilters.includeDelivered}
                onChange={toggleDemandFilter('includeDelivered')}
              />
            )}
            label={t('inventoryRequirements.demandFilter.delivered')}
          />
          <FormControlLabel
            control={(
              <Checkbox
                size="small"
                checked={params.demandFilters.includeReserved}
                onChange={toggleDemandFilter('includeReserved')}
              />
            )}
            label={t('inventoryRequirements.demandFilter.reserved')}
          />
          <FormControlLabel
            control={(
              <Checkbox
                size="small"
                checked={params.demandFilters.includeDeleted}
                onChange={toggleDemandFilter('includeDeleted')}
              />
            )}
            label={t('inventoryRequirements.demandFilter.deleted')}
          />
        </FormGroup>
      </Paper>

      {loadingSaved && (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loadingSaved && rows.length === 0 ? (
        <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
          {t('inventoryRequirements.empty')}
        </Typography>
      ) : (
        !loadingSaved && (
          <ResponsiveTable
            columns={columns}
            data={rows}
            renderMobileCard={renderMobileCard}
          />
        )
      )}
    </Box>
  );
};

export default React.memo(InventoryRequirements);
